/**
 * Roles & access swimlane (Oracle) — lives on User Lookup, not Sql Studio.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertCircle, RefreshCw, Shield, Map, Users, GitCompare, ChevronDown, ChevronUp } from 'lucide-react';
import { useCollapsibleStrip } from './CollapsibleStrip';
import UserSecurityAccessMap from './UserSecurityAccessMap';
import UserAccessCompare from './UserAccessCompare';
import {
  loadAndRunSecurityAccessMap,
  loadFusionUsersDirectory,
  auditUserRisk,
  checkUserSoD,
  SECURITY_ACCESS_MAP_CORE_RECIPE_ID,
  SECURITY_ACCESS_MAP_RECIPE_ID,
} from './securityAccessMapApi';
export default function UserLookupSecuritySection({
  /** 'security' | 'compare' — controlled by User Lookup tabs */
  panel = 'security',
  /** Fusion username from SCIM lookup — pre-selects combobox when map is loaded */
  focusUsername = '',
  /** SCIM effective roles from User Lookup — enriches map when SQL hierarchy is empty */
  scimRoles = null,
  /** Display name / person # for merged SCIM rows */
  scimUserMeta = null,
  /** Auto-load security map when a user is found (first time per session) */
  autoLoadOnUser = true,
}) {
  const [directoryUsers, setDirectoryUsers] = useState(null);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryError, setDirectoryError] = useState('');
  const [rows, setRows] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState('');
  const [selectedUsername, setSelectedUsername] = useState('');
  const [autoMapTried, setAutoMapTried] = useState(false);
  const [mapRecipeId, setMapRecipeId] = useState(SECURITY_ACCESS_MAP_CORE_RECIPE_ID);
  const [coreMapLoaded, setCoreMapLoaded] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState('');
  const [auditResult, setAuditResult] = useState(null);
  const [sodLoading, setSodLoading] = useState(false);
  const [sodError, setSodError] = useState('');
  const [sodResult, setSodResult] = useState(null);

  const loadDirectory = useCallback(async () => {
    setDirectoryLoading(true);
    setDirectoryError('');
    try {
      const result = await loadFusionUsersDirectory();
      setDirectoryUsers(result.users);
    } catch (err) {
      setDirectoryError(err.message || 'Failed to load employee directory');
      setDirectoryUsers(null);
    } finally {
      setDirectoryLoading(false);
    }
  }, []);

  const runSoDCheck = useCallback(async (username) => {
    const u = String(username || '').trim();
    if (!u) return;
    setSodLoading(true);
    setSodError('');
    try {
      const result = await checkUserSoD({ username: u });
      setSodResult(result);
    } catch (err) {
      setSodError(err.message || 'SoD check failed');
      setSodResult(null);
    } finally {
      setSodLoading(false);
    }
  }, []);

  const loadMap = useCallback(
    async (username, { recipeId = SECURITY_ACCESS_MAP_CORE_RECIPE_ID } = {}) => {
      const u = String(username || selectedUsername || focusUsername || '').trim();
      if (!u) {
        setMapError('Select an employee from the directory first.');
        return;
      }
      const rid = String(recipeId || SECURITY_ACCESS_MAP_CORE_RECIPE_ID).trim();
      setMapLoading(true);
      setMapError('');
      setSodResult(null);
      setSodError('');
      if (rid === SECURITY_ACCESS_MAP_CORE_RECIPE_ID) {
        setRows(null);
      }
      try {
        const meta = scimUserMeta || {};
        const result = await loadAndRunSecurityAccessMap({
          username: u,
          recipeId: rid,
          scimRoles: u === focusUsername && Array.isArray(scimRoles) ? scimRoles : null,
          userMeta: {
            displayName: meta.displayName || '',
            personNumber: meta.personNumber || '',
          },
        });
        setRows(result.rows);
        setSelectedUsername(u);
        setMapRecipeId(rid);
        if (rid === SECURITY_ACCESS_MAP_CORE_RECIPE_ID) {
          setCoreMapLoaded(true);
        }
        runSoDCheck(u);
      } catch (err) {
        setMapError(err.message || 'Failed to load access map');
        if (rid === SECURITY_ACCESS_MAP_CORE_RECIPE_ID) {
          setRows(null);
          setCoreMapLoaded(false);
        }
      } finally {
        setMapLoading(false);
      }
    },
    [selectedUsername, focusUsername, scimRoles, scimUserMeta, runSoDCheck]
  );

  useEffect(() => {
    loadDirectory();
  }, [loadDirectory]);

  useEffect(() => {
    if (!focusUsername) return;
    setSelectedUsername(focusUsername);
  }, [focusUsername]);

  useEffect(() => {
    if (panel !== 'security') return;
    if (!autoLoadOnUser || !focusUsername || autoMapTried || mapLoading) return;
    if (!directoryUsers?.length) return;
    if (!directoryUsers.some((u) => u.username === focusUsername)) return;
    setAutoMapTried(true);
    loadMap(focusUsername);
  }, [panel, autoLoadOnUser, focusUsername, autoMapTried, mapLoading, directoryUsers, loadMap]);

  const handleEmployeeSelect = useCallback(
    (username) => {
      setSelectedUsername(username);
      setAuditResult(null);
      setAuditError('');
      setSodResult(null);
      loadMap(username);
    },
    [loadMap]
  );

  const clearMapResults = useCallback(() => {
    setRows(null);
    setMapError('');
    setSodResult(null);
    setSodError('');
    setAuditResult(null);
    setAuditError('');
    setCoreMapLoaded(false);
    setSelectedUsername('');
  }, []);

  const clearDirectoryData = useCallback(() => {
    setDirectoryUsers(null);
    setDirectoryError('');
    setSelectedUsername('');
    clearMapResults();
    setAutoMapTried(false);
  }, [clearMapResults]);

  const handleSecurityAudit = useCallback(
    async (username) => {
      const u = String(username || selectedUsername || focusUsername || '').trim();
      if (!u) return;
      setAuditLoading(true);
      setAuditError('');
      setAuditResult(null);
      try {
        const meta = directoryUsers?.find((x) => x.username === u) || scimUserMeta || {};
        const userData = {
          displayName: meta.displayName || scimUserMeta?.displayName || '',
          personNumber: meta.personNumber || scimUserMeta?.personNumber || '',
        };
        const result = await auditUserRisk({
          username: u,
          mapRows: Array.isArray(rows) && rows.length ? rows : null,
          userData,
          useExtended: mapRecipeId === SECURITY_ACCESS_MAP_RECIPE_ID,
        });
        setAuditResult(result);
        if (!result.ok && result.rawText) {
          setAuditError('Model response was not valid JSON — partial results shown.');
        }
      } catch (err) {
        setAuditError(err.message || 'Security audit failed');
        setAuditResult(null);
      } finally {
        setAuditLoading(false);
      }
    },
    [selectedUsername, focusUsername, rows, mapRecipeId, directoryUsers, scimUserMeta]
  );

  const hasRows = Array.isArray(rows) && rows.length > 0;
  const hasDirectory = Array.isArray(directoryUsers) && directoryUsers.length > 0;
  const isCompare = panel === 'compare';
  const isExtended = mapRecipeId === SECURITY_ACCESS_MAP_RECIPE_ID;
  const canClearMap =
    hasRows ||
    !!mapError ||
    !!sodResult ||
    !!sodError ||
    !!auditResult ||
    !!auditError ||
    !!selectedUsername;
  const canClearDirectory = hasDirectory || !!directoryError || !!selectedUsername;

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <SectionHeader
        panel={panel}
        hasRows={hasRows}
        hasDirectory={hasDirectory}
        directoryCount={directoryUsers?.length ?? 0}
        directoryLoading={directoryLoading}
        mapLoading={mapLoading}
        mapRecipeId={mapRecipeId}
        coreMapLoaded={coreMapLoaded}
        onRefreshDirectory={loadDirectory}
        onLoadMap={() => loadMap()}
        onLoadExtended={() => loadMap(undefined, { recipeId: SECURITY_ACCESS_MAP_RECIPE_ID })}
        onClearMap={clearMapResults}
        onClearDirectory={clearDirectoryData}
        canClearMap={canClearMap}
        canClearDirectory={canClearDirectory}
      />

      {isCompare && hasDirectory ? (
        <div className="border-b border-slate-100 px-4 py-4">
          <UserAccessCompare
            key={`compare-${hasDirectory}-${directoryUsers?.length ?? 0}-${selectedUsername}`}
            pickerUsers={directoryUsers}
            initialUserA={selectedUsername || focusUsername}
            initialUserB=""
            useExtended={isExtended}
            accent="cyan"
          />
        </div>
      ) : null}

      {directoryError ? (
        <div className="flex items-start gap-2 border-b border-slate-100 px-4 py-3 text-sm text-amber-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p>Employee directory: {directoryError}</p>
            <button
              type="button"
              onClick={loadDirectory}
              disabled={directoryLoading}
              className="mt-2 text-xs font-medium text-cyan-700 underline hover:text-cyan-800 disabled:opacity-50"
            >
              Retry directory
            </button>
          </div>
        </div>
      ) : null}

      {!isCompare && mapError ? (
        <div className="flex items-start gap-2 border-b border-slate-100 px-4 py-3 text-sm text-rose-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p>{mapError}</p>
            <button
              type="button"
              onClick={() => loadMap()}
              disabled={mapLoading}
              className="mt-2 text-xs font-medium text-cyan-700 underline hover:text-cyan-800 disabled:opacity-50"
            >
              Retry map
            </button>
          </div>
        </div>
      ) : null}

      {directoryLoading && !hasDirectory ? (
        <LoadingState label="Loading employee directory…" />
      ) : null}

      {!hasDirectory && !directoryLoading && !directoryError ? (
        <div className="px-4 py-8 text-center text-sm text-slate-500">
          <Map className="mx-auto mb-2 h-8 w-8 text-slate-300" />
          <p>No employees returned from the Fusion users directory.</p>
        </div>
      ) : null}

      {!isCompare && (hasDirectory || hasRows) ? (
        <div className="border-t border-slate-200 p-4">
          <UserSecurityAccessMap
            rows={rows || []}
            employeePickerUsers={directoryUsers || []}
            selectedUsername={selectedUsername}
            initialUsername={focusUsername || selectedUsername}
            onEmployeeSelect={handleEmployeeSelect}
            onSecurityAudit={handleSecurityAudit}
            securityAuditLoading={auditLoading}
            auditResult={auditResult}
            sodConflicts={sodResult?.violations || sodResult?.conflicts || []}
            sodLoading={sodLoading}
            accent="cyan"
          />
          {sodError ? (
            <p className="mt-2 text-xs text-rose-600">{sodError}</p>
          ) : null}
          {auditError ? (
            <p className="mt-2 text-sm text-rose-800">{auditError}</p>
          ) : null}
          {auditResult?.rawText ? (
            <pre className="mt-2 max-h-32 overflow-auto rounded border border-slate-200 bg-slate-50 p-2 text-[10px] text-slate-600">
              {auditResult.rawText}
            </pre>
          ) : null}
          {mapLoading ? (
            <LoadingState label="Loading security details for selected employee…" />
          ) : null}
        </div>
      ) : null}

      {!isCompare && !hasDirectory && !directoryLoading && directoryError && focusUsername ? (
        <div className="px-4 py-6 text-center text-sm text-slate-500">
          <button
            type="button"
            onClick={() => loadMap(focusUsername)}
            disabled={mapLoading}
            className="inline-flex items-center gap-2 rounded-lg bg-cyan-700 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-800 disabled:bg-slate-300"
          >
            <Shield className="h-4 w-4" />
            Load security map for looked-up user
          </button>
        </div>
      ) : null}
    </div>
  );
}

function SectionHeader({
  panel = 'security',
  hasRows,
  hasDirectory,
  directoryCount,
  directoryLoading,
  mapLoading,
  mapRecipeId,
  coreMapLoaded,
  onRefreshDirectory,
  onLoadMap,
  onLoadExtended,
  onClearMap,
  onClearDirectory,
  canClearMap = false,
  canClearDirectory = false,
}) {
  const busy = directoryLoading || mapLoading;
  const isExtended = mapRecipeId === SECURITY_ACCESS_MAP_RECIPE_ID;
  const isCompare = panel === 'compare';
  const TitleIcon = isCompare ? GitCompare : Shield;
  const title = isCompare ? 'Compare users' : 'Roles, access & SoD';
  const { expanded, toggle } = useCollapsibleStrip(`aiTool.userLookup.security.${panel}`, true);

  const actionButtons = (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={onRefreshDirectory}
        disabled={busy}
        className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
      >
        {directoryLoading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <RefreshCw className="h-3.5 w-3.5" />
        )}
        Refresh directory
      </button>
      <button
        type="button"
        onClick={onClearDirectory}
        disabled={busy || !canClearDirectory}
        className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        title="Clear loaded Fusion directory and employee selection"
      >
        Clear directory
      </button>
      {!isCompare ? (
        <>
          <button
            type="button"
            onClick={onLoadMap}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md border border-cyan-200 bg-white px-3 py-1.5 text-xs font-medium text-cyan-700 hover:bg-cyan-50 disabled:opacity-60"
          >
            {mapLoading && !isExtended ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Shield className="h-3.5 w-3.5" />
            )}
            {hasRows ? 'Refresh core map' : 'Load security map'}
          </button>
          <button
            type="button"
            onClick={onClearMap}
            disabled={busy || !canClearMap}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            title="Clear map results, SoD banners, and errors"
          >
            Clear results
          </button>
          {coreMapLoaded || hasRows ? (
            <button
              type="button"
              onClick={onLoadExtended}
              disabled={busy}
              title="Person profiles, delegations, and inherited roles"
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-50 disabled:opacity-60"
            >
              {mapLoading && isExtended ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Users className="h-3.5 w-3.5" />
              )}
              Load extended person scope
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );

  if (!expanded) {
    return (
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50/90 px-4 py-1.5">
        <TitleIcon className={`h-4 w-4 shrink-0 ${isCompare ? 'text-violet-600' : 'text-cyan-600'}`} />
        <span className="text-sm font-semibold text-slate-700">{title}</span>
        {hasDirectory ? (
          <span className="truncate text-[11px] text-slate-500">
            {directoryCount.toLocaleString()} Fusion accounts
          </span>
        ) : null}
        <button
          type="button"
          onClick={toggle}
          className="ml-auto inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-100"
          aria-expanded={false}
        >
          <ChevronDown className="h-3.5 w-3.5" />
          Expand
        </button>
      </div>
    );
  }

  return (
    <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <TitleIcon className={`h-4 w-4 shrink-0 ${isCompare ? 'text-violet-600' : 'text-cyan-600'}`} />
            {title}
          </div>
        {hasDirectory ? (
          <p className="mt-1 text-[10px] text-slate-400">
            {directoryCount.toLocaleString()} accounts
          </p>
        ) : null}
        </div>
        <button
          type="button"
          onClick={toggle}
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-100"
          aria-expanded
        >
          <ChevronUp className="h-3.5 w-3.5" />
          Minimize
        </button>
      </div>
      <div className="mt-2">{actionButtons}</div>
    </div>
  );
}

function LoadingState({ label = 'Loading…' }) {
  return (
    <div className="flex items-center justify-center gap-2 px-4 py-12 text-sm text-slate-500">
      <Loader2 className="h-5 w-5 animate-spin text-cyan-600" />
      {label}
    </div>
  );
}
